import { MetaTagConfig } from './metaTags';

/**
 * JSON-LD Structured Data for SEO
 * Helps search engines understand the content and context of pages
 */

export interface StructuredData {
    '@context': string;
    '@type': string;
    [key: string]: any;
}

// Organization Schema
export const ORGANIZATION_SCHEMA: StructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'BhriguGPT',
    description: 'AI-Powered Vedic Astrology Platform offering Free Kundli, Horoscope, and Panchang services',
    url: 'https://bhrigugpt.com',
    logo: {
        '@type': 'ImageObject',
        url: 'https://bhrigugpt.com/bhrigoo_rishi.png',
        width: 512,
        height: 512
    },
    sameAs: [
        // Add social media links here when available
    ],
    contactPoint: {
        '@type': 'ContactPoint',
        email: 'support@bhrigugpt.com',
        contactType: 'Customer Support',
        availableLanguage: ['English', 'Hindi']
    }
};

// Website Schema
export const WEBSITE_SCHEMA: StructuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'BhriguGPT',
    url: 'https://bhrigugpt.com',
    description: 'AI-Powered Vedic Astrology Platform offering Free Kundli, Horoscope, and Panchang services'
};

// SoftwareApplication Schema (avoids AggregateRating without verified reviews)
export const SOFTWARE_APP_SCHEMA: StructuredData = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'BhriguGPT',
    description: 'AI-Powered Vedic Astrology Platform offering Free Kundli, Horoscope, and Panchang services',
    url: 'https://bhrigugpt.com',
    applicationCategory: 'LifestyleApplication',
    operatingSystem: 'Web',
    offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'INR'
    }
};

// Service Schema for Astrology Services
export const generateServiceSchema = (serviceName: string, description: string, url: string): StructuredData => ({
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: serviceName,
    description: description,
    provider: {
        '@type': 'Organization',
        name: 'BhriguGPT'
    },
    url: url,
    areaServed: 'Worldwide',
    availableLanguage: ['English', 'Hindi']
});

// Breadcrumb Schema
export const generateBreadcrumbSchema = (items: Array<{ name: string; url: string }>): StructuredData => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: item.url
    }))
});

// FAQ Schema
export const generateFAQSchema = (faqs: Array<{ question: string; answer: string }>): StructuredData => ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer
        }
    }))
});

// Helper function to inject structured data into the page
export const injectStructuredData = (schema: StructuredData | StructuredData[]) => {
    const schemas = Array.isArray(schema) ? schema : [schema];

    // Remove existing structured data scripts
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach(script => script.remove());

    // Add new structured data
    schemas.forEach(schemaData => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.text = JSON.stringify(schemaData);
        document.head.appendChild(script);
    });
};

// Predefined schemas for each page
export const PAGE_SCHEMAS = {
    landing: [
        ORGANIZATION_SCHEMA,
        WEBSITE_SCHEMA,
        SOFTWARE_APP_SCHEMA,
        generateServiceSchema(
            'Vedic Astrology Services',
            'Comprehensive Vedic astrology services including Kundli generation, daily horoscope, and Panchang',
            'https://bhrigugpt.com'
        )
    ],

    freeKundli: [
        ORGANIZATION_SCHEMA,
        generateServiceSchema(
            'Free Kundli Online',
            'Generate your free Kundli (birth chart) with accurate Vedic astrology calculations',
            'https://bhrigugpt.com/free-kundli'
        ),
        generateBreadcrumbSchema([
            { name: 'Home', url: 'https://bhrigugpt.com' },
            { name: 'Free Kundli', url: 'https://bhrigugpt.com/free-kundli' }
        ])
    ],

    horoscope: [
        ORGANIZATION_SCHEMA,
        generateServiceSchema(
            'Daily Horoscope',
            'Get accurate daily, weekly, and monthly horoscope predictions for all zodiac signs',
            'https://bhrigugpt.com/horoscope'
        ),
        generateBreadcrumbSchema([
            { name: 'Home', url: 'https://bhrigugpt.com' },
            { name: 'Horoscope', url: 'https://bhrigugpt.com/horoscope' }
        ])
    ],

    panchang: [
        ORGANIZATION_SCHEMA,
        generateServiceSchema(
            'Vedic Panchang',
            'Check today\'s Panchang with Tithi, Nakshatra, Yoga, and auspicious timings',
            'https://bhrigugpt.com/panchang'
        ),
        generateBreadcrumbSchema([
            { name: 'Home', url: 'https://bhrigugpt.com' },
            { name: 'Panchang', url: 'https://bhrigugpt.com/panchang' }
        ])
    ]
};
